import { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { loadUpcomingTrainerSessions } from "../../../utils/trainerSessionUtils";
import authAPI from "../../../services/authApi";
import { useAdminHubUi } from "../../../hooks/useAdminAppMode";
import { formatDateRu, formatTimeRu } from "../../../utils/appointmentLabels";
import StatusBadge from "../ui/StatusBadge";
import AdminDoctorDataForm from "./AdminDoctorDataForm";
import AdminDoctorBranchesForm from "./AdminDoctorBranchesForm";
import TrainerGroupSchedule from "./TrainerGroupSchedule";
import AdminDoctorCalendar from "./AdminDoctorCalendar";
import TrainerProfilePwa from "./TrainerProfilePwa";
import {
  DOCTOR_TABS,
  EMPTY_EDIT,
  doctorToEdit,
  doctorPhotoUrl,
  DOCTORS_BASE,
  doctorBranchOptionsFromResponse,
  formatTrainerGroups,
} from "./doctorConstants";
import s from "../clinic.module.css";
import {
  Button,
  Card,
  Tabs,
  Avatar,
  EmptyState,
  useToast,
} from "../../ui";

const VALID_TABS = new Set(DOCTOR_TABS.map((t) => t.id));

function sessionTitle(session) {
  if (session?.title?.trim()) return session.title;
  if (session?.group?.name) return session.group.name;
  if (session?.individualClient?.full_name) {
    return `Индивидуально · ${session.individualClient.full_name}`;
  }
  return session?.session_kind === "event" ? "Событие" : "Занятие";
}

function formatMonthLabel(ym) {
  if (!ym || !/^\d{4}-\d{2}$/.test(ym)) return "этом месяце";
  const [y, m] = ym.split("-").map(Number);
  const label = new Date(y, m - 1, 1).toLocaleDateString("ru-RU", {
    month: "long",
    year: "numeric",
  });
  return label;
}

function sessionsWord(n) {
  const abs = Math.abs(Number(n) || 0) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return "занятий";
  if (d === 1) return "занятие";
  if (d >= 2 && d <= 4) return "занятия";
  return "занятий";
}

function hoursWord(n) {
  const abs = Math.abs(Number(n) || 0) % 100;
  const d = abs % 10;
  if (abs > 10 && abs < 20) return "часов";
  if (d === 1) return "час";
  if (d >= 2 && d <= 4) return "часа";
  return "часов";
}

export default function AdminDoctorProfile() {
  const { doctorId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const isApp = useAdminHubUi();

  const tabFromUrl = searchParams.get("tab");
  const doctorTab = VALID_TABS.has(tabFromUrl) ? tabFromUrl : "overview";

  const setDoctorTab = (id) => {
    const next = new URLSearchParams(searchParams);
    if (id === "overview") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
  };

  const [detail, setDetail] = useState(null);
  const [edit, setEdit] = useState(EMPTY_EDIT);
  const [branches, setBranches] = useState([]);
  const [groupCatalog, setGroupCatalog] = useState([]);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [workload, setWorkload] = useState(null);

  const loadDoctor = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    clinicAPI
      .getDoctor(doctorId)
      .then((r) => {
        setDetail(r);
        const branchOptions = doctorBranchOptionsFromResponse(r.doctor);
        setEdit(doctorToEdit(r.doctor, branchOptions));
        setBranches(branchOptions);
      })
      .catch((e) => {
        toast.error(e.response?.data?.error || "Тренер не найден");
        navigate(isApp ? "/manage/app/team" : DOCTORS_BASE);
      })
      .finally(() => setLoading(false));
  }, [doctorId, navigate, toast, isApp]);

  useEffect(() => {
    clinicAPI
      .getSpecializations()
      .then((r) =>
        setGroupCatalog((r.specializations || []).filter((g) => g.is_active !== false)),
      )
      .catch(() => {});
  }, []);

  useEffect(() => {
    loadDoctor();
  }, [loadDoctor]);

  useEffect(() => {
    if (!doctorId) return;
    const month = new Date().toISOString().slice(0, 7);
    clinicAPI.getDoctorWorkload(doctorId, month).then(setWorkload);
  }, [doctorId]);

  const saveDoctor = async (e) => {
    e?.preventDefault?.();
    try {
      await clinicAPI.updateDoctor(doctorId, {
        full_name: edit.full_name,
        description: edit.description,
        email: edit.email,
        is_active: edit.is_active,
        branchIds: edit.branchIds,
        groupIds: edit.groupIds,
        experience_years: edit.experience_years,
        max_appointments_per_day: edit.max_appointments_per_day,
        min_break_minutes: edit.min_break_minutes,
        substitute_doctor_id: edit.substitute_doctor_id || null,
        notify_appointments: edit.notify_appointments,
        mis_external_id: edit.mis_external_id,
      });
      toast.success("Сохранено");
      loadDoctor();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const saveBranches = async () => {
    try {
      await clinicAPI.updateDoctor(doctorId, {
        branchIds: edit.branchIds,
        groupIds: edit.groupIds,
      });
      toast.success("Назначения сохранены");
      loadDoctor();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const deactivate = async () => {
    let upcoming = 0;
    try {
      upcoming = await loadUpcomingTrainerSessions(doctorId);
    } catch {
      upcoming = 0;
    }
    const lines = [
      "Деактивировать тренера?",
      "Профиль скроется из записи, но вход в приложение останется, если аккаунт не заблокирован в разделе «Команда».",
    ];
    if (upcoming > 0) {
      lines.push(
        `У тренера ${upcoming} предстоящих занятий — они останутся в календаре, пока вы их не отмените.`,
      );
    }
    if (!window.confirm(lines.join("\n\n"))) return;
    try {
      await clinicAPI.deactivateDoctor(doctorId);
      toast.success("Тренер деактивирован");
      loadDoctor();
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const setPassword = async () => {
    const userId = detail?.doctor?.user_id;
    if (!userId) {
      toast.error("Нет привязанного аккаунта");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("Пароль от 8 символов");
      return;
    }
    try {
      await authAPI.adminSetUserPassword(userId, newPassword);
      setNewPassword("");
      toast.success("Пароль обновлён");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    }
  };

  const toggleBranch = (id) => {
    setEdit((prev) => ({
      ...prev,
      branchIds: prev.branchIds.includes(id)
        ? prev.branchIds.filter((x) => x !== id)
        : [...prev.branchIds, id],
    }));
  };

  if (loading && !detail) {
    return (
      <div className={s.patientPage}>
        <p className={s.timelineLoading}>Загрузка…</p>
      </div>
    );
  }

  const doctor = detail?.doctor;
  const summary = detail?.summary;

  if (isApp) {
    return (
      <TrainerProfilePwa
        doctorId={doctorId}
        doctor={doctor}
        edit={edit}
        setEdit={setEdit}
        branches={branches}
        groupCatalog={groupCatalog}
        workload={workload}
        summary={summary}
        loading={loading}
        newPassword={newPassword}
        setNewPassword={setNewPassword}
        onSaveDoctor={saveDoctor}
        onSaveBranches={saveBranches}
        onSetPassword={setPassword}
        onDeactivate={deactivate}
        onReload={loadDoctor}
      />
    );
  }

  return (
    <div className={s.patientPage}>
      <nav className={`${s.patientBreadcrumbs} ${s.patientPageBlock}`}>
        <Link to="/manage/app/team">Команда</Link>
        <span aria-hidden>/</span>
        <span>{doctor?.full_name || "Тренер"}</span>
      </nav>

      <header className={`${s.patientProfileHeader} ${s.patientPageBlock}`}>
        <div className={s.patientProfileIdentity}>
          <Avatar
            name={doctor?.full_name}
            src={doctorPhotoUrl(doctor?.photo_url)}
            size="lg"
          />
          <div>
            <h1 className={s.patientProfileName}>
              {doctor?.full_name || "Тренер"}
            </h1>
            <div className={s.patientProfileMeta}>
              {formatTrainerGroups(doctor) ? (
                <span>{formatTrainerGroups(doctor)}</span>
              ) : (
                <span>Группа не назначена</span>
              )}
              {doctor?.rating_avg != null && (
                <span>
                  ★ {Number(doctor.rating_avg).toFixed(1)} ({doctor.rating_count})
                </span>
              )}
              {doctor?.email && <span>{doctor.email}</span>}
              <span>{doctor?.is_active ? "Активен" : "Неактивен"}</span>
            </div>
          </div>
        </div>
        <div className={s.patientProfileActions}>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setDoctorTab("schedule")}
          >
            Слоты
          </Button>
        </div>
      </header>

      <Card className={s.patientPageBlock}>
        <Tabs
          items={DOCTOR_TABS}
          active={doctorTab}
          onChange={setDoctorTab}
        />

        {doctorTab === "overview" && (
          <>
            <div className={s.doctorStatsRow}>
              <div className={s.doctorStat}>
                <div className={s.doctorStatLabel}>Сегодня</div>
                <div className={s.doctorStatValue}>
                  {summary?.today_count ?? 0}
                </div>
                <div className={s.doctorStatHint}>
                  {sessionsWord(summary?.today_count ?? 0)}
                </div>
              </div>
              <div className={s.doctorStat}>
                <div className={s.doctorStatLabel}>Эта неделя</div>
                <div className={s.doctorStatValue}>
                  {summary?.week_count ?? 0}
                </div>
                <div className={s.doctorStatHint}>
                  {sessionsWord(summary?.week_count ?? 0)}
                </div>
              </div>
              <div className={s.doctorStat}>
                <div className={s.doctorStatLabel}>
                  {workload
                    ? formatMonthLabel(workload.month)
                    : "Этот месяц"}
                </div>
                <div className={s.doctorStatValue}>
                  {workload
                    ? workload.sessions_count ?? workload.appointments_count ?? 0
                    : "—"}
                </div>
                <div className={s.doctorStatHint}>
                  {workload
                    ? sessionsWord(
                        workload.sessions_count ?? workload.appointments_count,
                      )
                    : "занятий"}
                </div>
              </div>
              <div className={s.doctorStat}>
                <div className={s.doctorStatLabel}>Нагрузка за месяц</div>
                <div className={s.doctorStatValue}>
                  {workload ? workload.total_hours : "—"}
                </div>
                <div className={s.doctorStatHint}>
                  {workload ? hoursWord(workload.total_hours) : "часов"}
                </div>
              </div>
            </div>
            <p
              className={
                summary?.has_schedule
                  ? s.doctorNextAppt
                  : s.doctorNextApptMuted
              }
            >
              {summary?.has_schedule
                ? "Тренер назначен на слоты групп — дни и время смотрите в календаре."
                : "Слоты групп ещё не назначены — отметьте их во вкладке «Слоты»."}
            </p>
            {summary?.next_appointment ? (
              <p className={s.doctorNextAppt}>
                Ближайшее:{" "}
                {formatDateRu(summary.next_appointment.start_datetime)} ·{" "}
                {formatTimeRu(summary.next_appointment.start_datetime)} —{" "}
                {sessionTitle(summary.next_appointment)}
              </p>
            ) : (
              <p className={s.doctorNextApptMuted}>Нет предстоящих занятий</p>
            )}

            <h3 className={s.timelineSectionTitle}>Ближайшие занятия</h3>
            <div className={s.apptList}>
              {(detail?.sessions || []).length === 0 ? (
                <EmptyState title="Нет предстоящих занятий" />
              ) : (
                (detail?.sessions || []).slice(0, 15).map((session) => (
                  <div key={session.id} className={s.apptCard}>
                    <div style={{ flex: 1 }}>
                      <div className={s.apptDate}>
                        {formatDateRu(session.start_datetime)} ·{" "}
                        {formatTimeRu(session.start_datetime)}
                        {session.end_datetime
                          ? `–${formatTimeRu(session.end_datetime)}`
                          : ""}
                      </div>
                      <div className={s.apptMeta}>{sessionTitle(session)}</div>
                      <div style={{ marginTop: 8 }}>
                        <StatusBadge status={session.status} />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {doctorTab === "data" && (
          <AdminDoctorDataForm
            edit={edit}
            setEdit={setEdit}
            onSubmit={saveDoctor}
            newPassword={newPassword}
            setNewPassword={setNewPassword}
            onSetPassword={setPassword}
            onDeactivate={deactivate}
            isActive={doctor?.is_active}
            userId={doctor?.user_id || null}
          />
        )}

        {doctorTab === "assignments" && (
          <AdminDoctorBranchesForm
            groups={groupCatalog}
            groupIds={edit.groupIds}
            onChangeAssignments={(patch) =>
              setEdit((prev) => ({ ...prev, ...patch }))
            }
            onSave={saveBranches}
          />
        )}

        {doctorTab === "calendar" && (
          <AdminDoctorCalendar
            doctorId={doctorId}
            doctorBranchOptions={branches}
          />
        )}

        {doctorTab === "schedule" && (
          <TrainerGroupSchedule
            doctorId={doctorId}
            trainerName={doctor?.full_name}
            assignedGroups={doctor?.groups || []}
            groupCatalog={groupCatalog}
            onScheduleSaved={loadDoctor}
          />
        )}
      </Card>
    </div>
  );
}
